import { useCallback, useMemo, useRef } from 'react';

import type { BaseEventPayload } from '@atlaskit/editor-common/analytics/api';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
import type { RovoSkillsInsertMenuConfig } from '@atlassian/rovo-skills/insert-menu-provider/types';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

export type EditorAPI = PublicPluginAPI<[AnalyticsPlugin, AIPlugin]> | null | undefined;

export type UseRovoSkillsConfigParams = {
	/** The Atlassian cloud ID for the current site. */
	cloudId: string;
	/** Whether Rovo AI is enabled for the user. */
	isRovoEnabled: boolean;
	/** The editor API reference, used to call handleSkillSelected and fireAnalyticsEvent. */
	editorAPI: EditorAPI;
	/** Content ID for checking AI content enablement. */
	contentId: string;
	/** Whether to show all available skills. Defaults to `true`. */
	showAllSkills?: boolean;
	/** Explicit list of skill slugs to show. When set, only these skills appear. */
	allowedSkills?: string[];
	/** Custom handler for when a skill is selected. Defaults to the AI plugin's `handleSkillSelected` action. */
	onSkillSelected?: (slashCommand: string, skill: unknown) => void;
	/** Custom analytics handler. Defaults to forwarding via the editor analytics plugin. */
	onAnalyticsEvent?: (payload: Record<string, unknown>) => void;
};

/**
 * Returns a `RovoSkillsInsertMenuConfig` for use in editor insert menus.
 * Handles gating, cloudId, and wires `onSkillSelected` and `onAnalyticsEvent`
 * to the editor API.
 *
 * Usage:
 * ```ts
 * const rovoSkillsConfig = useRovoSkillsConfig({ cloudId, isRovoEnabled, editorAPI, contentId });
 * ```
 */
export function useRovoSkillsConfig({
	cloudId,
	isRovoEnabled,
	editorAPI,
	contentId,
	showAllSkills = true,
	allowedSkills,
	onSkillSelected,
	onAnalyticsEvent,
}: UseRovoSkillsConfigParams): RovoSkillsInsertMenuConfig | undefined {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	// Keep a ref to the latest editorAPI so callbacks always read the
	// current value at invocation time, regardless of when the config
	// was memoised or cached by upstream consumers.
	const editorAPIRef = useRef<EditorAPI>(editorAPI);
	editorAPIRef.current = editorAPI;

	const defaultOnAnalyticsEvent = useCallback((payload: Record<string, unknown>) => {
		const api = editorAPIRef.current;
		api?.analytics?.actions?.fireAnalyticsEvent<BaseEventPayload, 'customEventType'>(
			payload as BaseEventPayload,
		);
	}, []);

	return useMemo(() => {
		// Read isInDocumentStreamingEnabled from the AI plugin shared state.
		// This value is set once during plugin initialisation and does not change,
		// so a synchronous read inside useMemo is safe.
		const aiState = editorAPI?.aiExperience?.sharedState?.currentState();
		const isInDocumentStreamingEnabled =
			aiState?.aiDecorationExperiencePluginState?.isInDocumentStreamingEnabled ?? false;

		if (
			!isRovoEnabled ||
			!isAIEnabledForContent ||
			!cloudId ||
			!isInDocumentStreamingEnabled ||
			!isExperimentEnabled('platform_editor_insert_menu_ai')
		) {
			return undefined;
		}

		const handleSkillSelected =
			onSkillSelected ??
			((
				slashCommand: string,
				skill: { [key: string]: unknown; id: string; name: string; slug: string },
			) => {
				setTimeout(() => {
					const api = editorAPIRef.current;
					const command = api?.aiExperience?.actions?.handleSkillSelected(slashCommand, skill);
					if (command !== undefined) {
						api?.core?.actions.execute(command);
					}
				}, 0);
			});

		return {
			enabled: true,
			cloudId,
			showAllSkills,
			allowedSkills,
			onSkillSelected: handleSkillSelected,
			onAnalyticsEvent: onAnalyticsEvent ?? defaultOnAnalyticsEvent,
		};
	}, [
		isRovoEnabled,
		isAIEnabledForContent,
		cloudId,
		editorAPI,
		showAllSkills,
		allowedSkills,
		onSkillSelected,
		onAnalyticsEvent,
		defaultOnAnalyticsEvent,
	]);
}
